import { Link } from "@remix-run/react";
import { useId, useState } from "react";

import { useCountry } from "~/context/CountryContext";
import { useLanguage } from "~/context/LanguageContext";
import {
  COURSE_TYPES,
  CourseType,
  FEATURED_INTENSITY,
  INTENSITIES,
  getDiscountedPrice,
  getPricingFor,
} from "~/data/pricing";
import { useLocalizedPath } from "~/helpers/navigationHelper";
import common from "~/i18n/common";
import pages from "~/i18n/pages";
import pricing from "~/i18n/pricing";
import { useT } from "~/i18n/useT";

import "./PricingTiers.scss";

export default function PricingTiers() {
  const t = useT(pricing);
  const p = useT(pages);
  const c = useT(common);
  const { country } = useCountry();
  const { lang } = useLanguage();
  const localize = useLocalizedPath();
  const groupId = useId();

  const [courseType, setCourseType] = useState<CourseType>("groupCourses");

  const tabLabel: Record<CourseType, string> = {
    groupCourses: p.pricingPage.group,
    individualCourses: p.pricingPage.individual,
  };

  return (
    <>
      <div className="ss-plan-toggle">
        {/* A radiogroup, not two buttons: assistive tech should hear that these
            are two states of one choice, and which one is active. */}
        <div
          className="ss-plan-toggle__group"
          role="radiogroup"
          aria-label={p.pricingPage.planLabel}
        >
          {COURSE_TYPES.map((type) => (
            <button
              key={type}
              type="button"
              role="radio"
              aria-checked={courseType === type}
              id={`${groupId}-${type}`}
              className={`ss-plan-toggle__tab${
                courseType === type ? " is-active" : ""
              }`}
              onClick={() => setCourseType(type)}
            >
              {tabLabel[type]}
            </button>
          ))}
        </div>
      </div>

      <div className="ss-tiers">
        {INTENSITIES.map((intensity) => {
          const featured = intensity === FEATURED_INTENSITY;
          const tier = t[courseType][intensity];

          return (
            <article
              key={intensity}
              className={`ss-tier${featured ? " ss-tier--featured" : ""}`}
            >
              {featured ? (
                <span className="ss-badge ss-tier__badge">
                  {p.pricingPage.popular}
                </span>
              ) : null}

              <h3 className="ss-tier__name">{t.tierNames[intensity]}</h3>

              <p className="ss-tier__price">
                <span className="ss-tier__amount">
                  {getDiscountedPrice(country, courseType, intensity, lang)}
                </span>
                <s className="ss-tier__was">
                  {getPricingFor(country, courseType, intensity, lang)}
                </s>
                <span className="ss-tier__per">{p.pricingPage.perMonth}</span>
              </p>

              <p className="ss-tier__desc">{tier.description}</p>

              <hr className="ss-tier__rule" />

              <ul className="ss-tier__features">
                {tier.features.map((feature) => (
                  <li key={feature}>
                    <span aria-hidden="true">✓</span>
                    {feature}
                  </li>
                ))}
              </ul>

              <Link
                to={localize("/contact")}
                className={`ss-btn ss-tier__cta ${
                  featured ? "ss-btn--accent" : "ss-btn--dark"
                }`}
              >
                {c.cta.purchaseNow}
              </Link>
            </article>
          );
        })}
      </div>

      {/* PAngV wants "includes VAT" next to consumer prices, and saying it
          here covers both pages that render the tiers. */}
      <p className="ss-tiers__note">{p.pricingPage.priceNote}</p>
    </>
  );
}
